/**
 * Reels — estate agents turn a property's finished Sneak Peek into a Price Reduction or Sold reel.
 * No clip picking: the original edit, brand template and logo are reused; only the message and music change.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Film, TrendingDown, Tag, Music2, Play, Pause, Search, Loader2, Check, Download,
  RefreshCw, Library, AlertTriangle, ChevronLeft, Clapperboard, Sparkles,
} from "lucide-react";
import { AppHeader } from "./AppHeader";
import "./AgentReels.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KINDS = [
  { key: "price_reduction", label: "Price Reduction", blurb: "Was / now pricing with the saving called out", Icon: TrendingDown },
  { key: "recently_sold", label: "Sold", blurb: "Sold or Sold STC — and a nudge to the next seller", Icon: Tag },
];
const STATUS_COPY = {
  preparing: "Preparing clips — matching colours",
  submitting: "Sending to the renderer",
  planned: "Queued at the renderer",
  waiting: "Queued at the renderer",
  transcribing: "Rendering",
  rendering: "Rendering your reel",
  succeeded: "Done",
  failed: "Render failed",
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");
const reelTitle = (r) => (r.kind === "price_reduction" ? "Price Reduction" : r.sold_label || "Sold");
// mirrors the server's _fmt_price: "450000" / "450,000" / "450k" → "£450,000"
const fmtPrice = (raw) => {
  const s = (raw || "").trim();
  if (!s) return "";
  const mult = /k$/i.test(s) ? 1000 : 1;
  const digits = s.replace(/[^\d]/g, "");
  return digits ? `£${(parseInt(digits, 10) * mult).toLocaleString("en-GB")}` : s.toUpperCase();
};
const priceValue = (s) => { const d = (s || "").replace(/[^\d]/g, ""); return d ? parseInt(d, 10) : null; };
const twoLine = (name) => {
  const words = (name || "").toUpperCase().split(/\s+/).filter(Boolean);
  if (words.length < 2) return [words.join(" ")];
  let best = 1, diff = Infinity;
  for (let i = 1; i < words.length; i++) {
    const d = Math.abs(words.slice(0, i).join(" ").length - words.slice(i).join(" ").length);
    if (d < diff) { diff = d; best = i; }
  }
  return [words.slice(0, best).join(" "), words.slice(best).join(" ")];
};

export default function AgentReels({ onBack }) {
  const [sources, setSources] = useState(null);
  const [brand, setBrand] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [kind, setKind] = useState("price_reduction");
  const [oldPrice, setOldPrice] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [soldLabel, setSoldLabel] = useState("Sold");
  const [music, setMusic] = useState(null); // null = same as the Sneak Peek
  const [pickingMusic, setPickingMusic] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [job, setJob] = useState(null);

  const loadSources = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/reels/sources`);
      setSources(r.data.sources || []);
      setBrand(r.data.brand || null);
    } catch (e) {
      setSources([]);
      toast.error(e.response?.data?.detail || "Couldn't load your properties");
    }
  }, []);
  useEffect(() => { loadSources(); }, [loadSources]);

  const selected = useMemo(() => (sources || []).find((s) => s.source_id === selectedId) || null, [sources, selectedId]);

  useEffect(() => {
    if (!job || (job.done && !job.library_pending)) return undefined;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/reels/status/${job.batch_id}`);
        setJob(r.data);
        if (r.data.done && !r.data.library_pending) loadSources();
      } catch { /* keep polling */ }
    }, 4000);
    return () => clearInterval(t);
  }, [job, loadSources]);

  const pickSource = (s) => { setSelectedId(s.source_id); setJob(null); setPickingMusic(false); };

  const canRender = selected && !submitting && (kind !== "price_reduction" || (oldPrice.trim() && newPrice.trim()));

  const render = async () => {
    if (!canRender) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/reels/render`, {
        source_id: selected.source_id, kind, old_price: oldPrice, new_price: newPrice, sold_label: soldLabel,
        music_track_url: music ? music.url : null, music_track_name: music ? music.name : null,
      });
      const j = r.data.jobs?.[0];
      setJob({ ...j, batch_id: r.data.batch_id, done: false, in_library: false, library_pending: false, kind, sold_label: soldLabel });
      toast.success(`${kind === "price_reduction" ? "Price Reduction" : soldLabel} reel is on its way`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start the reel");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="App" data-testid="reels-page">
      <div className="container">
        <AppHeader onBack={onBack} />
        <div className="ar-head">
          <div className="ar-head-icon"><Film size={22} /></div>
          <div>
            <h1 className="ar-title" data-testid="reels-title">Reels</h1>
            <p className="ar-sub">Turn a property's finished Sneak Peek into a Price Reduction or Sold reel — same footage, your brand, new message. Finished reels land in your Social Library.</p>
          </div>
        </div>

        <section className="ar-panel" data-testid="reels-sources">
          <div className="ar-step"><span className="ar-step-no">1</span><h2>Which property?</h2>
            {selected && <button type="button" className="ar-link" onClick={() => { setSelectedId(null); setJob(null); }} data-testid="reels-change-source"><ChevronLeft size={14} /> Change property</button>}
          </div>
          {sources === null ? (
            <div className="ar-empty"><Loader2 size={16} className="animate-spin" /> Finding your finished Sneak Peeks…</div>
          ) : sources.length === 0 ? (
            <div className="ar-empty" data-testid="reels-empty"><Clapperboard size={16} /> No finished Sneak Peeks yet — as soon as the editing team renders one for your branch it will appear here.</div>
          ) : (
            <div className={`ar-sources ${selected ? "is-collapsed" : ""}`}>
              {(selected ? [selected] : sources).map((s) => <SourceCard key={s.source_id} s={s} active={s.source_id === selectedId} onPick={pickSource} />)}
            </div>
          )}
        </section>

        {selected && (
          <>
            <section className="ar-panel" data-testid="reels-message">
              <div className="ar-step"><span className="ar-step-no">2</span><h2>What's the news?</h2></div>
              <div className="ar-kinds">
                {KINDS.map(({ key, label, blurb, Icon }) => (
                  <button key={key} type="button" className={`ar-kind ${kind === key ? "is-on" : ""}`} onClick={() => setKind(key)} data-testid={`reels-kind-${key}`}>
                    <Icon size={20} /><strong>{label}</strong><small>{blurb}</small>
                    {selected.brand_templates?.[key] ? <span className="ar-brand-tag">Your brand template</span>
                      : brand ? <span className="ar-brand-tag">Styled as {brand.name}</span> : null}
                  </button>
                ))}
              </div>
              <div className="ar-message-grid">
                {kind === "price_reduction" ? (
                  <div className="ar-prices">
                    <label><span>Original price</span><div className="ar-money"><i>£</i><input inputMode="numeric" placeholder="450,000" value={oldPrice} onChange={(e) => setOldPrice(e.target.value)} data-testid="reels-old-price" /></div></label>
                    <label><span>New price</span><div className="ar-money"><i>£</i><input inputMode="numeric" placeholder="425,000" value={newPrice} onChange={(e) => setNewPrice(e.target.value)} data-testid="reels-new-price" /></div></label>
                    <p className="ar-hint">WMS doesn't hold prices, so type both — the reel shows the saving automatically.</p>
                  </div>
                ) : (
                  <div className="ar-sold">
                    {["Sold", "Sold STC"].map((l) => (
                      <button key={l} type="button" className={`ar-chip ${soldLabel === l ? "is-on" : ""}`} onClick={() => setSoldLabel(l)} data-testid={`reels-sold-label-${l === "Sold" ? "sold" : "stc"}`}>{soldLabel === l && <Check size={13} />} {l}</button>
                    ))}
                    <p className="ar-hint">Sold STC swaps the wording on the sold card; everything else stays as designed.</p>
                  </div>
                )}
                <ReelPreview kind={kind} oldPrice={oldPrice} newPrice={newPrice} soldLabel={soldLabel} property={selected.property} brand={brand} />
              </div>
            </section>

            <section className="ar-panel" data-testid="reels-music">
              <div className="ar-step"><span className="ar-step-no">3</span><h2>Music</h2></div>
              <div className="ar-music-row">
                <button type="button" className={`ar-chip ${!music ? "is-on" : ""}`} onClick={() => { setMusic(null); setPickingMusic(false); }} data-testid="reels-music-same">
                  <Music2 size={13} /> Same as the Sneak Peek{selected.music_name ? ` · ${selected.music_name}` : ""}
                </button>
                <button type="button" className={`ar-chip ${music ? "is-on" : ""}`} onClick={() => setPickingMusic((v) => !v)} data-testid="reels-music-pick">
                  <Sparkles size={13} /> {music ? `Own track · ${music.name}` : "Choose a different track"}
                </button>
              </div>
              {pickingMusic && <TrackPicker value={music} onPick={(t) => { setMusic(t); setPickingMusic(false); }} />}
            </section>

            <section className="ar-panel ar-render" data-testid="reels-render">
              <div className="ar-render-row">
                <button type="button" className="ar-render-btn" disabled={!canRender} onClick={render} data-testid="reels-render-btn">
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <Film size={16} />} Render {kind === "price_reduction" ? "Price Reduction" : soldLabel} reel
                </button>
                <span className="ar-render-note">Usually 1–2 minutes. It'll appear in your Social Library when it's done.</span>
              </div>
              {job && <ReelResult job={job} onRetry={render} />}
            </section>

            {selected.reels.length > 0 && (
              <section className="ar-panel" data-testid="reels-existing">
                <div className="ar-step"><h2>Reels already made for {selected.property.name || "this property"}</h2></div>
                <div className="ar-existing">
                  {selected.reels.map((r) => (
                    <div key={r.job_id} className={`ar-existing-row is-${r.status}`} data-testid={`reels-existing-${r.job_id}`}>
                      {r.snapshot_url ? <img src={r.snapshot_url} alt="" /> : <span className="ar-existing-ph"><Film size={14} /></span>}
                      <div className="ar-existing-text">
                        <strong>{reelTitle(r)}{r.kind === "price_reduction" && r.old_price ? ` · ${r.old_price} → ${r.new_price}` : ""}</strong>
                        <small>{fmtDate(r.created_at)}{r.music_name ? ` · ♪ ${r.music_name}` : ""} · {STATUS_COPY[r.status] || r.status}</small>
                      </div>
                      {r.status === "succeeded" && r.url && <a className="ar-mini-btn" href={r.url} target="_blank" rel="noreferrer" data-testid={`reels-existing-open-${r.job_id}`}><Download size={13} /> Open</a>}
                      {r.library_asset_id && <span className="ar-lib-tag"><Library size={12} /> In library</span>}
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SourceCard({ s, active, onPick }) {
  const made = s.reels.filter((r) => r.status === "succeeded");
  return (
    <button type="button" className={`ar-source ${active ? "is-on" : ""}`} onClick={() => onPick(s)} data-testid={`reels-source-${s.source_id}`}>
      <div className="ar-source-thumb">{s.thumb ? <img src={s.thumb} alt="" loading="lazy" /> : <Clapperboard size={20} />}</div>
      <div className="ar-source-text">
        <strong>{s.property.name || s.address || `Order ${s.wms_job_id}`}</strong>
        <small>{[s.property.location, s.wms_job_id ? `WMS #${s.wms_job_id}` : null].filter(Boolean).join(" · ")}</small>
        <small>{s.preview_kind === "just_listed" ? "Just Listed reel" : "Sneak Peek"} · {fmtDate(s.created_at)}{s.clip_count ? ` · ${s.clip_count} clips` : ""}</small>
        {made.length > 0 && <span className="ar-source-made">{made.map((r) => reelTitle(r)).filter((v, i, a) => a.indexOf(v) === i).join(" · ")} made</span>}
      </div>
      {active && <Check size={16} className="ar-source-check" />}
    </button>
  );
}

export function TrackPicker({ value, onPick, withBeat = false }) {
  const [tracks, setTracks] = useState(null);
  const [bpmByUrl, setBpmByUrl] = useState({});
  const [q, setQ] = useState("");
  const [playing, setPlaying] = useState(null);
  const audio = useRef(null);
  useEffect(() => {
    axios.get(`${API}/music/search`, { params: { page_size: 100 } })
      .then((r) => setTracks(r.data.tracks || []))
      .catch(() => setTracks([]));
    if (withBeat) {
      axios.get(`${API}/music/beat-mapped`)
        .then((r) => setBpmByUrl(Object.fromEntries((r.data.tracks || []).filter((t) => t.url).map((t) => [t.url, t.bpm]))))
        .catch(() => {});
    }
    return () => { audio.current?.pause(); };
  }, [withBeat]);
  const toggle = (t) => {
    if (playing === t.id) { audio.current?.pause(); setPlaying(null); return; }
    audio.current?.pause();
    audio.current = new Audio(t.url);
    audio.current.play().catch(() => {});
    audio.current.onended = () => setPlaying(null);
    setPlaying(t.id);
  };
  const shown = (tracks || []).filter((t) => !q || t.name.toLowerCase().includes(q.toLowerCase()) || t.vibe?.includes(q.toLowerCase()));
  return (
    <div className="ar-tracks" data-testid="reels-track-picker">
      <div className="ar-track-search"><Search size={14} /><input placeholder="Search by name or vibe" value={q} onChange={(e) => setQ(e.target.value)} data-testid="reels-track-search" /></div>
      {tracks === null ? <div className="ar-empty"><Loader2 size={14} className="animate-spin" /> Loading tracks…</div> : (
        <ul>
          {shown.slice(0, 60).map((t) => (
            <li key={t.id} className={value?.url === t.url ? "is-on" : ""}>
              <button type="button" className="ar-track-play" onClick={() => toggle(t)} aria-label="Preview" data-testid={`reels-track-play-${t.id}`}>{playing === t.id ? <Pause size={13} /> : <Play size={13} />}</button>
              <button type="button" className="ar-track-pick" onClick={() => onPick({ url: t.url, name: t.name, bpm: bpmByUrl[t.url] || null })} data-testid={`reels-track-${t.id}`}>
                <strong>{t.name}</strong><small>{t.vibe}{t.duration ? ` · ${Math.round(t.duration)}s` : ""}{withBeat && bpmByUrl[t.url] ? ` · ${bpmByUrl[t.url]} BPM · beat-mapped` : ""}</small>
              </button>
            </li>
          ))}
          {shown.length === 0 && <li className="ar-empty">No tracks match "{q}"</li>}
        </ul>
      )}
    </div>
  );
}

function ReelResult({ job, onRetry }) {
  const failed = job.status === "failed";
  const done = job.status === "succeeded";
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.get(`${API}/social-assets/${job.library_asset_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${reelTitle(job).replace(/\s+/g, "_")}_reel.mp4`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Saved to your device");
    } catch { toast.error("Download failed — try Open instead"); } finally { setSaving(false); }
  };
  return (
    <div className={`ar-result ${done ? "is-done" : ""} ${failed ? "is-failed" : ""}`} data-testid="reels-result">
      <div className="ar-result-video">
        {done && job.url ? <video src={job.url} poster={job.snapshot_url || undefined} controls playsInline data-testid="reels-result-video" /> : (
          <div className="ar-result-wait">{failed ? <AlertTriangle size={22} /> : <Loader2 size={22} className="animate-spin" />}<span>{STATUS_COPY[job.status] || job.status}…</span></div>
        )}
      </div>
      <div className="ar-result-side">
        <strong data-testid="reels-status">{failed ? "Something went wrong" : done ? `${reelTitle(job)} reel ready` : STATUS_COPY[job.status] || "Working"}</strong>
        {failed && <small className="ar-error">{job.error || "The renderer rejected this one."}</small>}
        {!failed && !done && <small>Hard cuts and music are matched to the original Sneak Peek. You can leave this page — the reel will still land in your Social Library.</small>}
        {done && (
          <div className="ar-result-actions">
            {job.in_library ? (
              <span className="ar-lib-tag is-big" data-testid="reels-library-badge"><Check size={13} /> In Social Library</span>
            ) : job.library_pending ? (
              <span className="ar-lib-tag is-big" data-testid="reels-library-pending"><Loader2 size={13} className="animate-spin" /> Adding to Social Library</span>
            ) : null}
            {job.in_library ? (
              <button type="button" className="ar-mini-btn is-primary" onClick={save} disabled={saving} data-testid="reels-download-btn">{saving ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Download</button>
            ) : (
              <a className="ar-mini-btn is-primary" href={job.url} target="_blank" rel="noreferrer" data-testid="reels-download-btn"><Download size={13} /> Open video</a>
            )}
            <button type="button" className="ar-mini-btn is-soon" disabled title="Coming soon — pushes this reel into the right place on WMS" data-testid="reels-wms-sync"><RefreshCw size={13} /> WMS Sync <em>soon</em></button>
          </div>
        )}
        {failed && <button type="button" className="ar-mini-btn is-primary" onClick={onRetry} data-testid="reels-retry-btn"><RefreshCw size={13} /> Try again</button>}
      </div>
    </div>
  );
}

function ReelPreview({ kind, oldPrice, newPrice, soldLabel, property, brand }) {
  const primary = brand?.primary || "#0F271A";
  const accent = brand?.accent || "#96C11F";
  const neutral = brand?.neutral || "#FFFFFF";
  const heroFont = brand?.hero_font || "Anton, Impact, sans-serif";
  const supportFont = brand?.support_font || "Montserrat, sans-serif";
  const name = twoLine(property?.name || "Your property");
  const location = (property?.location || "").toUpperCase();
  const isPR = kind === "price_reduction";
  const was = fmtPrice(oldPrice), now = fmtPrice(newPrice);
  const o = priceValue(was), n = priceValue(now);
  const saving = o && n && o > n ? `NOW £${(o - n).toLocaleString("en-GB")} LESS` : "NEW PRICE";
  const bad = o !== null && n !== null && n >= o;
  const stc = /stc/i.test(soldLabel || "");
  return (
    <div className="ar-preview-wrap" data-testid="reels-text-preview">
      <span className="ar-preview-label">What the reel will say{brand ? ` · ${brand.name} colours` : ""}</span>
      <div className="ar-preview" style={{ "--p": primary, "--a": accent, "--n": neutral, "--hero": heroFont, "--support": supportFont }}>
        {isPR ? (
          <>
            <span className="ar-pv-eyebrow">PRICE REDUCTION</span>
            <span className="ar-pv-name">{name.map((l, i) => <span key={i}>{l}</span>)}</span>
            {location && <span className="ar-pv-loc">{location}</span>}
            <div className="ar-pv-prices">
              <span className={`ar-pv-was ${was ? "" : "is-empty"}`} data-testid="reels-preview-was">WAS {was || "£—"}</span>
              <span className={`ar-pv-now ${now ? "" : "is-empty"}`} data-testid="reels-preview-now">NOW {now || "£—"}</span>
            </div>
            <span className={`ar-pv-pill ${bad ? "is-bad" : ""}`} data-testid="reels-preview-saving">{bad ? "NEW PRICE MUST BE LOWER" : was && now ? saving : "TYPE BOTH PRICES"}</span>
          </>
        ) : (
          <>
            <span className="ar-pv-eyebrow">RECENTLY</span>
            <span className="ar-pv-sold" data-testid="reels-preview-sold">{stc ? "SOLD STC." : "SOLD."}</span>
            <span className="ar-pv-name">{name.map((l, i) => <span key={i}>{l}</span>)}</span>
            {location && <span className="ar-pv-loc">{location}</span>}
            <span className="ar-pv-pill">YOUR HOME COULD BE NEXT</span>
          </>
        )}
      </div>
    </div>
  );
}
